import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import { Database, Cpu, Sparkles, ArrowRight } from 'lucide-react';

const CoTPretrainingSlide = () => {
  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="How reasoning capabilities develop from pre-training through reinforcement learning"
      >
        CoT Evolution: Pre-training to RL
      </SlideTitle>
      
      <SlideContent>
        {/* Timeline visualization */}
        <div className="flex flex-wrap justify-center items-center gap-4 mb-8">
          <div className="slide-card text-center px-6 py-4">
            <Database className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
            <div className="font-heading font-semibold text-foreground">Pre-Training</div>
            <div className="text-xs text-muted-foreground">Latent Capability</div>
          </div>
          <ArrowRight className="w-5 h-5 text-muted-foreground" />
          <div className="slide-card text-center px-6 py-4 border-secondary/30">
            <Cpu className="w-6 h-6 text-secondary mx-auto mb-2" />
            <div className="font-heading font-semibold text-foreground">Pure RL</div>
            <div className="text-xs text-secondary">Emergent CoT</div>
          </div>
          <ArrowRight className="w-5 h-5 text-muted-foreground" />
          <div className="slide-card text-center px-6 py-4 border-primary/30">
            <Sparkles className="w-6 h-6 text-primary mx-auto mb-2" />
            <div className="font-heading font-semibold text-foreground">Cold Start + RL</div>
            <div className="text-xs text-primary">Refined CoT</div>
          </div>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Pre-training */}
          <div className="slide-card">
            <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-muted-foreground" />
              Pre-Training: The Foundation
            </h3>
            
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-sm font-semibold text-foreground mb-1">Data Composition</p>
                <p className="text-sm text-muted-foreground">
                  DeepSeek-V3-Base: <span className="text-primary font-mono">14.8 trillion tokens</span>
                </p>
                <ul className="text-xs text-muted-foreground mt-2 space-y-1">
                  <li>• Plain web pages, e-books</li>
                  <li>• <strong className="text-foreground">Substantial math and code content</strong></li>
                  <li>• No synthetic CoT data added</li>
                </ul>
              </div>
              
              <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                <p className="text-sm font-semibold text-primary mb-1">Why Math & Code Matter</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Inherently contain reasoning traces</li>
                  <li>• Step-by-step proofs in math</li>
                  <li>• Logic structures in code</li>
                  <li>• Develops internal "iteration heads"</li>
                </ul>
              </div>
              
              <div className="p-2 rounded bg-muted/30 text-xs text-muted-foreground">
                <strong>Result:</strong> Model has latent capability to generate solutions, but reasoning is not explicitly activated.
              </div>
            </div>
          </div>
          
          {/* RL Evolution */}
          <div className="slide-card">
            <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
              <Cpu className="w-5 h-5 text-secondary" />
              Pure RL: The Evolution
            </h3>
            
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-secondary/10 border border-secondary/20">
                <p className="text-sm font-semibold text-secondary mb-1">Spontaneous Behavior Changes</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Response length: <span className="text-foreground">hundreds → thousands</span> of tokens</li>
                  <li>• Model learns longer thoughts = better rewards</li>
                  <li>• Explores strategies, backtracks, verifies</li>
                </ul>
              </div>
              
              <div className="p-3 rounded-lg bg-accent/10 border border-accent/20">
                <p className="text-sm font-semibold text-accent mb-1">Key Metric: AIME 2024</p>
                <div className="flex items-center justify-between mt-2">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Before "Aha"</p>
                    <p className="text-xl font-mono text-muted-foreground">15.6%</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-accent" />
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">After "Aha"</p>
                    <p className="text-xl font-mono text-accent">77.9%</p>
                  </div>
                </div>
              </div>
              
              <div className="p-2 rounded bg-muted/30 text-xs text-muted-foreground">
                <strong>Key Insight:</strong> Without being taught HOW to reason, the model learned that more thinking = correct answers.
              </div>
            </div>
          </div>
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default CoTPretrainingSlide;
